
import React, { useState, useRef } from 'react';
import { User } from '../types';
import Button from './Button';
import { saveUserToDB, exportAllData, importData } from '../services/dbService';

interface ProfileViewProps {
  t: any;
  user: User;
  onUpdate: (user: User) => void;
  onLogout: () => void;
  onClose: () => void;
}

const ProfileView: React.FC<ProfileViewProps> = ({ t, user, onUpdate, onLogout, onClose }) => {
  const [username, setUsername] = useState(user.username);
  const [email, setEmail] = useState(user.email);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const updatedUser = { ...user, username, email };
      await saveUserToDB(updatedUser);
      onUpdate(updatedUser);
      alert(t.copy === 'Copié' ? "Profil synchronisé !" : "Profile synchronized!");
    } catch (err) {
      alert("Erreur de synchronisation.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExport = () => {
    exportAllData(user.id);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await importData(file, user.id);
        alert("Contes importés et synchronisés !");
        window.location.reload();
      } catch (err) {
        alert("Erreur lors de l'importation.");
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-indigo-950/20 backdrop-blur-sm flex items-center justify-center p-6 z-[60] animate-fadeIn">
      <div className="max-w-md w-full bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 relative border border-indigo-50">
        <button onClick={onClose} className="absolute top-8 right-8 text-indigo-300 hover:text-indigo-600 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>

        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-4 rounded-3xl flex items-center justify-center text-4xl text-white font-bold shadow-xl" style={{ backgroundColor: user.avatarColor }}>
            {user.username.charAt(0).toUpperCase()}
          </div>
          <h2 className="text-2xl font-bold text-indigo-950">{t.profile}</h2>
          <p className="text-indigo-500 text-[10px] font-black uppercase tracking-widest mt-2 flex items-center justify-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Cloud Synchronisé
          </p>
          <p className="text-indigo-300 text-xs mt-1">Inscrit le {new Date(user.joinedAt).toLocaleDateString()}</p>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-indigo-900 uppercase ml-2">{t.name}</label>
            <input 
              value={username} 
              onChange={(e) => setUsername(e.target.value)}
              className="w-full p-4 bg-indigo-50 rounded-2xl outline-none focus:bg-white border border-transparent focus:border-indigo-100 transition-all"
            />
          </div>
          
          <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 space-y-3">
             <p className="text-[10px] font-bold text-indigo-800 uppercase tracking-widest">Outils de gestion</p>
             <div className="flex gap-2">
                <button onClick={handleExport} className="flex-1 py-2 bg-white text-indigo-700 text-[10px] font-black uppercase rounded-xl border border-indigo-200 hover:bg-indigo-100 transition-colors">
                  Exporter JSON
                </button>
                <button onClick={handleImportClick} className="flex-1 py-2 bg-white text-indigo-700 text-[10px] font-black uppercase rounded-xl border border-indigo-200 hover:bg-indigo-100 transition-colors">
                  Importer JSON
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="hidden" />
             </div>
          </div>

          <div className="pt-4 flex flex-col gap-3">
            <Button onClick={handleSave} isLoading={isUpdating} className="w-full py-4">
              {t.saveProfile}
            </Button>
            <button 
              onClick={onLogout}
              className="w-full py-4 text-red-500 font-bold hover:bg-red-50 rounded-2xl transition-colors"
            >
              {t.logout}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
